/**
 * AuthShell — full-page chrome for auth screens (Option A: split panel).
 *
 * Layout (all auth screens share it):
 *   - Start side (45%, lg+ only): deep-teal brand panel — full OVACS logo
 *     top-start, multi-line tagline with a brand-bright accent line,
 *     value-prop bullets, the OVACS mark as a large gradient watermark
 *     bleeding off the bottom-end corner, and a slowly drifting fine-line
 *     pattern. No blur/glassmorphism anywhere.
 *   - End side (55%): the form column on the system surface. Clean flat
 *     inputs/buttons (system primitives), theme + language toggles
 *     top-end, optional back button above the form.
 *
 * Panel copy (tagline / accent / bullets, per language) is managed by
 * superadmins at /superadmin/auth-screen and fetched from the public
 * /auth-panel/public/ endpoint. If the fetch fails or the admin disabled
 * it, the bundled i18n defaults under `auth.panel.*` render instead —
 * the login screen never blanks.
 *
 * Props are unchanged from the previous shell so pages don't need logic
 * edits: `variant` and `marketing` are still accepted but no longer
 * change the layout (every screen gets the same split shell; the old
 * marketing carousel is retired).
 *
 * Arabic: all text inherits Cairo via the global html[lang="ar"] rule —
 * nothing here sets a display font.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineSun,
  HiOutlineMoon,
  HiOutlineLanguage,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import { useTheme } from '../../contexts/ThemeContext'
import { useDirection } from '../../contexts/DirectionContext'
import { fetchPublicAuthPanel } from '../../services/authPanel'
import ovacsIcon from '../../assets/images/icons/Icon.svg'
import ovacsLogo from '../../assets/images/logos/ovacs-logo.svg'

// ─── Panel content (admin-managed, i18n fallback) ───────────────────────

function useAuthPanelContent() {
  const { t } = useTranslation()
  const { lang } = useDirection()
  const [remote, setRemote] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetchPublicAuthPanel()
      .then((data) => { if (!cancelled) setRemote(data) })
      .catch(() => { /* fall back to bundled copy */ })
    return () => { cancelled = true }
  }, [])

  const fallback = {
    tagline: t('auth.panel.tagline'),
    tagline_accent: t('auth.panel.taglineAccent'),
    bullets: [
      t('auth.panel.bullet1'),
      t('auth.panel.bullet2'),
      t('auth.panel.bullet3'),
    ],
  }

  if (!remote || remote.is_enabled === false) return fallback

  // Admin content is used verbatim (an admin clearing a field means
  // "don't show it") — the fallback only covers fetch failure/disabled.
  const block = remote.content?.[lang] || remote.content?.en
  if (!block) return fallback
  return {
    tagline: block.tagline || '',
    tagline_accent: block.tagline_accent || '',
    bullets: Array.isArray(block.bullets) ? block.bullets.filter(Boolean) : [],
  }
}

// ─── Brand panel (start side, lg+) ──────────────────────────────────────

function BrandPanel() {
  const content = useAuthPanelContent()
  const taglineLines = (content.tagline || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)

  return (
    <aside
      className="
        relative hidden lg:flex flex-col justify-between overflow-hidden
        lg:sticky lg:top-0 lg:h-screen
        p-12 xl:p-14 text-white
        bg-gradient-to-br from-[#0a3540] to-[#125064]
        dark:from-[#052028] dark:to-[#0b3947]
      "
    >
      {/* Drifting fine-line pattern */}
      <div
        aria-hidden="true"
        className="absolute -inset-1/2 pointer-events-none bg-[repeating-linear-gradient(115deg,rgba(255,255,255,0.04)_0px,rgba(255,255,255,0.04)_1px,transparent_1px,transparent_90px)] motion-safe:animate-auth-panel-lines"
      />

      {/* Watermark — the OVACS mark, bleeding off the bottom-end corner */}
      <img
        src={ovacsIcon}
        alt=""
        aria-hidden="true"
        className="absolute -bottom-32 -end-32 w-[30rem] h-[30rem] opacity-50 pointer-events-none select-none"
      />

      {/* Full logo, top-start */}
      <a href="/" className="relative z-10 inline-block w-fit" aria-label="OVACS">
        <img src={ovacsLogo} alt="OVACS" className="h-11 w-auto" />
      </a>

      {/* Tagline */}
      {(taglineLines.length > 0 || content.tagline_accent) && (
        <h2 className="relative z-10 max-w-[24ch] text-[clamp(1.5rem,2.4vw,2.125rem)] font-semibold leading-[1.3]">
          {taglineLines.map((line) => (
            <span key={line} className="block">{line}</span>
          ))}
          {content.tagline_accent && (
            <span className="block text-brand-bright">{content.tagline_accent}</span>
          )}
        </h2>
      )}

      {/* Value-prop bullets */}
      <ul className="relative z-10 space-y-3.5">
        {content.bullets.map((bullet) => (
          <li key={bullet} className="flex items-center gap-3 text-sm text-white/85">
            <HiOutlineCheckCircle className="w-[18px] h-[18px] flex-shrink-0 text-brand-bright" aria-hidden="true" />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    </aside>
  )
}

// ─── Top chrome (form side) ─────────────────────────────────────────────

function TopChrome() {
  const { t } = useTranslation()
  const { resolvedTheme, toggle: toggleTheme } = useTheme()
  const { lang, toggle: toggleLang } = useDirection()
  const isDark = resolvedTheme === 'dark'

  const pill = `
    inline-flex items-center justify-center gap-1.5
    rounded-full border border-border bg-surface text-ink-secondary
    hover:bg-muted hover:border-border-strong hover:text-ink-primary
    transition-colors
    focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40
  `

  return (
    <header className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-5 sm:px-8 py-4">
      {/* Brand — only below lg (on lg+ the panel carries the logo) */}
      <button
        type="button"
        onClick={() => (window.location.href = '/')}
        className="lg:invisible flex items-center gap-2.5 text-ink-primary"
        aria-label={t('common.appName')}
      >
        <img src={ovacsIcon} alt="" className="w-8 h-8 object-contain" />
        <span className="hidden sm:inline text-base font-bold tracking-wide">
          {t('common.appName')}
        </span>
      </button>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={t('theme.toggleAriaLabel')}
          className={`${pill} w-9 h-9`}
        >
          {isDark ? <HiOutlineSun className="w-4 h-4" /> : <HiOutlineMoon className="w-4 h-4" />}
        </button>
        <button
          type="button"
          onClick={toggleLang}
          aria-label={t('language.toggleAriaLabel')}
          className={`${pill} h-9 px-3 text-xs font-semibold`}
        >
          <HiOutlineLanguage className="w-4 h-4" />
          {lang === 'en' ? 'AR' : 'EN'}
        </button>
      </div>
    </header>
  )
}

// ─── Back button (rendered in-flow above the form) ──────────────────────

export function BackButton({ to, onClick, className = '' }) {
  const navigate = useNavigate()
  const { isRtl } = useDirection()
  return (
    <button
      type="button"
      onClick={onClick || (() => (to ? navigate(to) : navigate(-1)))}
      aria-label="Back"
      className={`
        inline-flex items-center justify-center w-10 h-10 rounded-full
        border border-border bg-surface text-ink-secondary
        hover:bg-muted hover:border-border-strong hover:text-ink-primary
        transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40
        ${className}
      `}
    >
      <svg
        viewBox="0 0 24 24"
        className={`w-5 h-5 ${isRtl ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
  )
}

// ─── Shell ──────────────────────────────────────────────────────────────

export default function AuthShell({
  variant = 'split', // accepted for compatibility — layout no longer varies
  children,
  marketing,          // retired — accepted so existing pages don't break
  showBack = false,
  backTo,
  className = '',
}) {
  return (
    <div className={`relative min-h-screen bg-canvas ${className}`}>
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[45%_55%]">
        <BrandPanel />

        {/* Form side */}
        <div className="relative bg-canvas lg:bg-surface transition-colors">
          <TopChrome />
          <main className="min-h-screen flex items-center justify-center px-4 sm:px-8 pt-24 pb-12 lg:pt-20">
            <div className="w-full max-w-md">
              {showBack && <BackButton to={backTo} className="mb-6" />}
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}
